// haai-game-logic.js — How Awesome Am I exercise engine.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Exercise/HAAI/HAAIGameLogic.cs
//
// Pure state machine. No DOM imports, no fetch, no audio. Mirrors the C#
// engine's public API one-for-one: method names, field ordering, even
// internal helper names map back so `git diff` against the Unity port stays
// readable.
//
// Public API (matches HAAIGameLogic.cs):
//   new HAAIGameLogic(mmuQueue, sessionSize)                       → engine instance (legacy single-pool)
//   new HAAIGameLogic({ targets, decoyPool }, sessionSize)         → engine instance (Brief 080 two-pool)
//   begin()                                   → start (or restart) the session
//   tapChoice(index)                          → record a tap on choice [index]
//   answerSpelling(text)                      → record a typed answer (Type H)
//   skip()                                    → advance without scoring
//   currentTarget()                           → MMU at currentIndex
//   isChoiceEliminated(index)                 → bitmask test
//   buildSummary()                            → final HAAISummary
//   snapshot()                                → current HAAISnapshot
//
// Events (CustomEvent on the engine; match the C# `OnFoo` events):
//   "questionChanged" → detail = { target, choices, wrongMask }
//   "correct"         → detail = { answerIndex, attemptNumber }
//   "wrong"           → detail = { tappedIndex, remaining }      (tile modalities)
//                       detail = { kind: "spell", attemptNumber } (Type H)
//   "exhausted"       → detail = { correctIndex }                (tile modalities)
//                       detail = { kind: "spell", expected }     (Type H)
//   "sessionComplete" → detail = HAAISummary
//
// Architecture rule (CLAUDE.md): engines never reference UI; screens consume
// engines via state snapshots only. Keep this file pure.

/** @typedef {import("../data/mmu-loader.js").Mmu} Mmu */

// ─────────────────────────────────────────────────────────────
// Snapshot + summary types — mirror HAAIGameLogic.cs state
// ─────────────────────────────────────────────────────────────

/**
 * @typedef {object} HAAISnapshot
 * @property {number}     sessionSize
 * @property {number}     currentIndex
 * @property {boolean}    isCompleted
 * @property {number}     perFailIndex   0..3
 * @property {number}     attemptNumber  alias of perFailIndex (0=first, 1=second, 2=third)
 * @property {number}     answerIndex    index of target inside `choices`
 * @property {number}     wrongMask      bitmask: 1<<i means choice i was tapped wrong
 * @property {Mmu | null} target         current MMU under test
 * @property {Mmu[]}      choices        4-up choices for the round
 * @property {number}     firstTryCount
 * @property {number}     secondTryCount
 * @property {number}     thirdTryCount
 * @property {number}     failedCount
 * @property {number}     recognitionIndex  0..100, rounded — iOS "Recognition
 *                                           Index" surfaced for the Lesson Report
 *                                           (Brief 095). Same value as
 *                                           HAAISummary.recognitionScore.
 */

/**
 * @typedef {object} HAAISummary
 * @property {number} totalTested
 * @property {number} firstTry
 * @property {number} secondTry
 * @property {number} thirdTry
 * @property {number} failed
 * @property {number} recognitionScore  0..100, rounded
 * @property {Mmu[]}  needsCorrection   MMUs that exhausted all 3 attempts
 */

// ─────────────────────────────────────────────────────────────
// Engine — mirror HAAIGameLogic.cs
// ─────────────────────────────────────────────────────────────

export class HAAIGameLogic extends EventTarget {
  /**
   * Two-pool form (Brief 080, CMS §8.1): targets are the met-only pool the
   * learner gets tested on; decoyPool is the wider met+unmet pool from which
   * distractors are drawn. The legacy single-array form remains supported —
   * it's equivalent to `{ targets: queue, decoyPool: queue }`.
   *
   * @param {Mmu[] | { targets: Mmu[], decoyPool: Mmu[] }} arg
   * @param {number} sessionSize
   */
  constructor(arg, sessionSize) {
    super();
    if (Array.isArray(arg)) {
      /** @type {Mmu[]} */ this._targets   = arg ?? [];
      /** @type {Mmu[]} */ this._decoyPool = arg ?? [];
    } else {
      this._targets   = arg?.targets   ?? [];
      this._decoyPool = arg?.decoyPool ?? this._targets;
    }
    // Kept for back-compat with any external `engine._queue` reads.
    /** @type {Mmu[]} */ this._queue = this._targets;
    this.sessionSize    = Math.min(sessionSize, this._targets.length);

    this.currentIndex   = 0;
    this.isCompleted    = false;
    this.perFailIndex   = 0;
    /** @type {number} */ this._wrongMask = 0;
    this.answerIndex    = 0;

    this.firstTryCount  = 0;
    this.secondTryCount = 0;
    this.thirdTryCount  = 0;
    this.failedCount    = 0;

    /** @type {Mmu | null} */ this._currentTarget = null;
    /** @type {Mmu[]} */      this._currentChoices = [];
    /** @type {Mmu[]} */      this._needsCorrection = [];

    this._reset();
  }

  // ─── Public actions ───────────────────────────────────────

  /** Start (or restart) the session. */
  begin() {
    this._reset();
    this._showCurrentQuestion();
  }

  /**
   * Record the learner's tap on choice [index].
   * @param {number} index
   */
  tapChoice(index) {
    if (this.isCompleted) return;

    const isRight = index === this.answerIndex;

    if (isRight) {
      this._recordCorrect();
      this.dispatchEvent(new CustomEvent("correct", {
        detail: { answerIndex: index, attemptNumber: this.perFailIndex + 1 },
      }));
      // Screen calls skip() after feedback delay
    } else {
      this._recordWrong(index);
      const remaining = this._countRemainingChoices();

      if (this.perFailIndex >= 3) {
        this.dispatchEvent(new CustomEvent("exhausted", {
          detail: { correctIndex: this.answerIndex },
        }));
        this._recordExhaustion();
        // Screen calls skip() after feedback delay
      } else {
        this.dispatchEvent(new CustomEvent("wrong", {
          detail: { tappedIndex: index, remaining },
        }));
      }
    }
  }

  /**
   * Type H — record a typed spelling. Compares case-insensitive, trimmed
   * (edges only), and treats curly apostrophe U+2019 as straight apostrophe
   * U+0027 (mirrors KFEHAAIHViewModel.m -isRightAnswerWithResult:).
   *
   * Streak rules match tapChoice: a wrong attempt within the question
   * increments perFailIndex but doesn't update firstTry/secondTry/thirdTry —
   * those only move on the final outcome. Three wrong attempts fire
   * `exhausted` and push the MMU into needsCorrection.
   *
   * @param {string} text
   */
  answerSpelling(text) {
    if (this.isCompleted) return;
    const target = this._currentTarget;
    if (!target) return;

    if (matchesSpelling(text, target.phrase)) {
      this._recordCorrect();
      this.dispatchEvent(new CustomEvent("correct", {
        detail: { kind: "spell", attemptNumber: this.perFailIndex + 1 },
      }));
      // Screen calls skip() after feedback delay
      return;
    }

    this.perFailIndex++;
    if (this.perFailIndex >= 3) {
      this.dispatchEvent(new CustomEvent("exhausted", {
        detail: { kind: "spell", expected: target.phrase },
      }));
      this._recordExhaustion();
      // Screen calls skip() after feedback delay
    } else {
      this.dispatchEvent(new CustomEvent("wrong", {
        detail: { kind: "spell", attemptNumber: this.perFailIndex },
      }));
    }
  }

  /**
   * Advance to the next question without scoring or firing correct/wrong
   * events. Use this after a feedback delay to move forward (avoids
   * re-entering tapChoice).
   */
  skip() {
    if (this.isCompleted) return;
    this._advance();
  }

  // ─── State queries ────────────────────────────────────────

  /** @returns {Mmu | null} */
  currentTarget() {
    if (this.currentIndex >= this._targets.length) return null;
    return this._targets[this.currentIndex];
  }

  /** @param {number} index @returns {boolean} */
  isChoiceEliminated(index) {
    return (this._wrongMask & (1 << index)) !== 0;
  }

  /** 0=first, 1=second, 2=third — alias of perFailIndex. */
  get attemptNumber() { return this.perFailIndex; }

  /**
   * Weighted recognition score, 0..100, rounded. iOS canon weighting per
   * Brief 003 (KFEHAAIViewModel.m): first-try × 1.0 + second × 0.66 + third
   * × 0.25, normalised over the session size. Shared by `buildSummary()` and
   * `snapshot()` so the value is computed once and never drifts (Brief 095 —
   * the "Recognition Index" the Lesson Report shows is this same number).
   * Pedagogy-ratified formula; do not re-tune (CLAUDE.md Rule #8).
   * @returns {number}
   */
  _recognitionScore() {
    const score = this.sessionSize > 0
      ? (this.firstTryCount * 1.0 + this.secondTryCount * 0.66 + this.thirdTryCount * 0.25)
        / this.sessionSize * 100
      : 0;
    return Math.round(score);
  }

  /** @returns {HAAISummary} */
  buildSummary() {
    return {
      totalTested:      this.sessionSize,
      firstTry:         this.firstTryCount,
      secondTry:        this.secondTryCount,
      thirdTry:         this.thirdTryCount,
      failed:           this.failedCount,
      recognitionScore: this._recognitionScore(),
      needsCorrection:  this._needsCorrection.slice(),
    };
  }

  /** @returns {HAAISnapshot} */
  snapshot() {
    return {
      sessionSize:    this.sessionSize,
      currentIndex:   this.currentIndex,
      isCompleted:    this.isCompleted,
      perFailIndex:   this.perFailIndex,
      attemptNumber:  this.perFailIndex,
      answerIndex:    this.answerIndex,
      wrongMask:      this._wrongMask,
      target:         this._currentTarget,
      choices:        this._currentChoices.slice(),
      firstTryCount:  this.firstTryCount,
      secondTryCount: this.secondTryCount,
      thirdTryCount:  this.thirdTryCount,
      failedCount:    this.failedCount,
      // Brief 095 — the canonical "Recognition Index" the Lesson Report reads.
      // Holds the same number as buildSummary().recognitionScore (renamed per
      // the May 2026 overseer doc §7; recognitionScore kept for back-compat).
      recognitionIndex: this._recognitionScore(),
    };
  }

  // ─── Internal ─────────────────────────────────────────────

  _reset() {
    this.currentIndex   = 0;
    this.isCompleted    = false;
    this.perFailIndex   = 0;
    this._wrongMask     = 0;
    this.answerIndex    = 0;
    this.firstTryCount  = this.secondTryCount = this.thirdTryCount = this.failedCount = 0;
    this._currentTarget  = null;
    this._currentChoices = [];
    this._needsCorrection = [];
  }

  _showCurrentQuestion() {
    if (this.currentIndex >= this.sessionSize) {
      this.isCompleted = true;
      const summary = this.buildSummary();
      this.dispatchEvent(new CustomEvent("sessionComplete", { detail: summary }));
      return;
    }

    this.perFailIndex = 0;
    this._wrongMask   = 0;

    const target  = this._targets[this.currentIndex];
    const choices = this._buildChoiceList(target);
    this.answerIndex = choices.indexOf(target);

    this._currentTarget  = target;
    this._currentChoices = choices;

    this.dispatchEvent(new CustomEvent("questionChanged", {
      detail: { target, choices: choices.slice(), wrongMask: this._wrongMask },
    }));
  }

  _recordCorrect() {
    switch (this.perFailIndex) {
      case 0: this.firstTryCount++;  break;
      case 1: this.secondTryCount++; break;
      case 2: this.thirdTryCount++;  break;
    }
  }

  /** @param {number} index */
  _recordWrong(index) {
    this._wrongMask |= (1 << index);
    this.perFailIndex++;
  }

  _recordExhaustion() {
    this.failedCount++;
    if (this._currentTarget) this._needsCorrection.push(this._currentTarget);
  }

  _advance() {
    this.currentIndex++;
    if (this.currentIndex >= this.sessionSize) {
      this.isCompleted = true;
      const summary = this.buildSummary();
      this.dispatchEvent(new CustomEvent("sessionComplete", { detail: summary }));
    } else {
      this._showCurrentQuestion();
    }
  }

  _countRemainingChoices() {
    let eliminated = 0;
    for (let i = 0; i < 4; i++) if (this.isChoiceEliminated(i)) eliminated++;
    return 4 - eliminated;
  }

  /**
   * Pick 3 distractors from the decoy pool (not the target), Fisher-Yates
   * shuffle them, and insert the target at a random position. Mirrors C#
   * BuildChoiceList.
   *
   * Brief 080: decoy pool is the wider met+unmet pool; the legacy single-pool
   * form aliases targets and decoyPool to the same array so distractors are
   * still drawn from co-targets only. Equality is by mmuId so a future
   * "let's deep-copy the pool" change can't silently break the target
   * exclusion when decoyPool contains a distinct object referring to the
   * same MMU.
   * @param {Mmu} target
   * @returns {Mmu[]}
   */
  _buildChoiceList(target) {
    const candidates = this._decoyPool.filter((m) => m.mmuId !== target.mmuId);

    // Fisher-Yates shuffle (matches the C# loop ordering)
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = candidates[i]; candidates[i] = candidates[j]; candidates[j] = tmp;
    }

    const distractors = candidates.slice(0, Math.min(3, candidates.length));
    const choices = distractors.slice();
    const insertAt = Math.floor(Math.random() * (choices.length + 1));
    choices.splice(insertAt, 0, target);
    return choices;
  }
}

// ─────────────────────────────────────────────────────────────
// Modality filter — mirror HAAIExerciseScreen.FilterMmusForModality
// ─────────────────────────────────────────────────────────────

/**
 * @typedef {"A"|"B"|"C"|"D"|"E"|"F"|"G"|"H"} HAAIModality
 */

/**
 * Drop MMUs that can't satisfy a modality. Each modality needs both stimulus
 * and response media, so the filter checks the union of both per-channel
 * requirements:
 *
 *   | Mod | Stimulus | Response | Drops MMU when                        |
 *   |-----|----------|----------|---------------------------------------|
 *   |  A  | audio    | image    | no audio OR no image                  |
 *   |  B  | image    | audio    | no image OR no audio                  |
 *   |  C  | audio    | text(t)  | no audio OR no target text (phrase)   |
 *   |  D  | text(t)  | audio    | no target text OR no audio            |
 *   |  E  | text(s)  | text(t)  | no source text (phraseZh) OR no phrase|
 *   |  F  | audio    | text(s)  | no audio OR no source text (phraseZh) |
 *   |  G  | text(s)  | image    | no source text OR no image            |
 *   |  H  | audio    | spelling | no audio OR no target text (phrase)   |
 *
 * Target language = English (phrase). Source language = Chinese (phraseZh).
 * Until the screen knows the learner's language at filter time, we treat
 * "target text" as `phrase` and "source text" as `phraseZh` — that maps to
 * the dataset (every MMU has phrase; not every MMU has phraseZh).
 *
 * @param {Mmu[]} pool
 * @param {HAAIModality} modality
 * @returns {Mmu[]}
 */
export function filterMmusForModality(pool, modality) {
  return pool.filter((m) => {
    const hasAudio  = !!m.audioFileName || !!m.audioStem;
    const hasImage  = !!m.imageFileName || !!m.imageStem;
    const hasTarget = !!m.phrase;
    const hasSource = !!m.phraseZh;
    switch (modality) {
      case "A": return hasAudio  && hasImage;
      case "B": return hasImage  && hasAudio;
      case "C": return hasAudio  && hasTarget;
      case "D": return hasTarget && hasAudio;
      case "E": return hasSource && hasTarget;
      case "F": return hasAudio  && hasSource;
      case "G": return hasSource && hasImage;
      case "H": return hasAudio && hasTarget;
      default:  return true;
    }
  });
}

// ─────────────────────────────────────────────────────────────
// Spelling comparison (Type H) — mirrors KFEHAAIHViewModel.m
// ─────────────────────────────────────────────────────────────

/**
 * Normalize a typed spelling for comparison: trim outer whitespace only
 * (inner spaces matter — "ice cream" ≠ "icecream"), lowercase, and fold
 * curly apostrophe U+2019 to straight U+0027.
 * @param {string} s
 * @returns {string}
 */
export function normalizeSpelling(s) {
  return String(s ?? "").trim().toLowerCase().replace(/’/g, "'");
}

/**
 * @param {string} typed
 * @param {string} expected
 * @returns {boolean}
 */
export function matchesSpelling(typed, expected) {
  return normalizeSpelling(typed) === normalizeSpelling(expected);
}
